import { type FormEvent, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import type { FlightLocationSuggestion } from "@b2bportal/core-flights";
import type {
  GuestsSelection,
  Suggestion as HotelSuggestion,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  createQueryParams,
  getPackageRoutes,
  HotelNavigationUtility,
} from "@overrides/utilities";
import dayjs from "dayjs";
import {
  PackagesRawEventName,
  useEmitCorePackagesRawEventOnce,
} from "../../../../../tracking";
import { PACKAGES_QUERY_PARAM } from "../../../../../utilities";
import { PackageSearchSelectors, PackagesSearchActions } from "../../../store";
import type { PackageSearchProps } from "../PackageSearch";

export const usePackageSearch = (props: PackageSearchProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useI18nContext();

  useEmitCorePackagesRawEventOnce({
    name: PackagesRawEventName.PackageSearchLoaded,
  });

  const dateRange = useSelector(PackageSearchSelectors.getSearchFormDateRange);
  const guests = useSelector(PackageSearchSelectors.getSearchFormGuests);
  const origin = useSelector(PackageSearchSelectors.getSearchFormOrigin);
  const destination = useSelector(
    PackageSearchSelectors.getSearchFormDestination
  );
  const roomCount = useSelector(PackageSearchSelectors.getSearchFormRoomCount);

  const hotelGuests = useMemo(
    () => ({ adults: guests.adultCount, children: guests.childAges }),
    [guests.adultCount, guests.childAges]
  );

  const handleDateRangeFromChange = (date: string | undefined) => {
    dispatch(PackagesSearchActions.setNewDateFrom(date));
  };
  const handleDateRangeUntilChange = (date: string | undefined) => {
    dispatch(PackagesSearchActions.setNewDateUntil(date));
  };

  const handleOriginChange = (location: FlightLocationSuggestion) => {
    dispatch(PackagesSearchActions.setNewOrigin(location));
  };
  const handleDestinationChange = (location: HotelSuggestion) => {
    dispatch(PackagesSearchActions.setNewDestination(location));
  };
  const handleGuestsChange = (guests: GuestsSelection) => {
    dispatch(
      PackagesSearchActions.setNewGuests({
        adultCount: guests.adults,
        childAges: guests.children,
        infantOnLapAges: [],
        infantInSeatAges: [],
      })
    );
  };
  const handleRoomCountChange = (roomCount: number) => {
    dispatch(PackagesSearchActions.setNewRoomCount(roomCount));
  };

  const isFormValid = useMemo(() => {
    return (
      dateRange?.from != null &&
      dateRange?.until != null &&
      destination != null &&
      origin != null
    );
  }, [dateRange?.from, dateRange?.until, destination, origin]);

  const handleSearch = useCallback(() => {
    if (
      isFormValid &&
      origin != null &&
      destination != null &&
      dateRange?.until != null &&
      dateRange?.from != null
    ) {
      const originCode = origin.id.code.code;
      const destinationCode = HotelNavigationUtility.placeLabelAndSublabelToUrl(
        destination.label,
        destination.subLabel
      );
      const searchParams = createQueryParams({
        [PACKAGES_QUERY_PARAM.fromDate]: dayjs(dateRange.from).toDate(),
        [PACKAGES_QUERY_PARAM.untilDate]: dayjs(dateRange.until).toDate(),
        [PACKAGES_QUERY_PARAM.adults]: guests.adultCount,
        [PACKAGES_QUERY_PARAM.destination]: destinationCode,
        [PACKAGES_QUERY_PARAM.origin]: originCode,
        [PACKAGES_QUERY_PARAM.children]: guests.childAges,
        [PACKAGES_QUERY_PARAM.roomsCount]: roomCount,
      });

      navigate(
        `${
          getPackageRoutes().FULL_ROUTES.HOTELS.AVAILABILITY
        }?${searchParams.toString()}`
      );
    }
  }, [
    isFormValid,
    origin,
    destination,
    dateRange.until,
    dateRange.from,
    guests,
    roomCount,
    navigate,
  ]);

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      props.onSubmit ? props.onSubmit() : handleSearch();
      props.onSubmitAppend?.();
    },
    [handleSearch, props]
  );

  return {
    context: {
      dateRange,
      guests,
      origin,
      destination,
      originLabel: t("core-packages.search.origin"),
      destinationLabel: t("core-packages.search.destination"),
      searchButtonLabel: t("core-hotels.search.button"),
      isFormValid,
      hotelGuests,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handleOriginChange,
      handleDestinationChange,
      handleGuestsChange,
      handleRoomCountChange,
      onFormSubmit,
      handleSearch,
    },
  };
};
